/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { ViewModel } from '../../../core/viewmodel/ViewModel';
import { SnackbarStore } from '../../../stores/application/SnackbarStore';
import { StoreProvider } from '../../../stores/StoreProvider';
import * as H from 'history';
import { AuthStore } from '../../../stores/application/AuthStore';
import { CourseService } from '../../../services/CourseService';
import { CourseModel } from '../../../models/components/CourseModel';
import { EventStore } from '../../../stores/application/EventStore';
import { EventTypes } from '../../../models/application/EventModel';
import { Course } from '../../../types/course/Course';

export class CoursesOverviewViewModel extends ViewModel {
	private readonly snackbarStore: SnackbarStore;
	private readonly authStore: AuthStore;
	private readonly eventStore: EventStore;
	private courseService: CourseService;
	private courseModel: CourseModel = new CourseModel();

	constructor(storeProvider: StoreProvider, history: H.History) {
		super(storeProvider, history);
		this.snackbarStore = storeProvider.snackbarStore;
		this.authStore = storeProvider.authStore;
		this.courseService = storeProvider.courseService;
		this.eventStore = storeProvider.eventStore;
		this.queryCourses();
	}

	public getAuthId(): string | undefined {
		return this.authStore.getAuthToken();
	}

	public getCourses(): Course[] {
		return this.courseModel.courses;
	}

	public setCourses(courses: Course[]): void {
		this.courseModel.setCourses(courses);
	}

	public subscribeToCourseEvents(): EventTypes[] {
		return this.eventStore.subscribeToEvents(
			[EventTypes.COURSE_CREATED, EventTypes.COURSE_UPDATED, EventTypes.COURSE_DELETED],
			this,
		);
	}

	public queryCourses(): Promise<void> | void {
		if (!this.authStore.hasAuthToken()) {
			return this.snackbarStore.showError('courses.query_courses.no_valid_authToken');
		}

		const authToken: string = this.authStore.getAuthToken();

		return this.courseService
			.getCourses(authToken)
			.then((courses: Course[]) => {
				this.setCourses(courses);
			})
			.catch(() => {
				this.snackbarStore.showError('courses.query_courses.query_error');
			});
	}
}
